import React, { useContext, useEffect, useState } from "react";
import Select from "~/shared/ui/Fields/Select";
import { useForm } from "react-hook-form/dist/index.ie11";
import Button from "~/shared/ui/Fields/Button";
import s from "../CameraZonesView.less";
import TextArea from "~/shared/ui/Fields/TextArea";
import { yupResolver } from "@hookform/resolvers/yup";
import { cameraCheckValidation } from "~/pages/CameraZonesView/utils/validation";
import { useTranslation } from "react-i18next";
import {
  CameraCheck,
  CameraCheckStatus,
  CameraStatus,
  Maybe,
  useAddNotificationMutation,
  useSetCameraCheckMutation,
} from "~/schemas/generated/graphql";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { NOTIFICATIONS } from "~/constans/notifications";
import { get } from "lodash";
import { CameraStatusUpdateContext } from "~/pages/CameraZonesView/modals/CameraViewModal";

const options: {
  label: string;
  value: CameraCheckStatus | CameraStatus | number;
}[] = [
  { label: "Не выбрано", value: 0 },
  { label: "Соответствует", value: CameraCheckStatus.Compliant },
  { label: "Не соответствует", value: CameraCheckStatus.NotCompliant },
];

interface ICameraCheckAcceptingProps {
  onlyNotCompliant: number;
  currentStatus: Maybe<CameraStatus>;
}

const CameraCheckAccepting = ({
  onlyNotCompliant,
  currentStatus,
}: ICameraCheckAcceptingProps) => {
  const { t } = useTranslation();
  const { cameraId } = useURLParamsGetter<{ cameraId: string }>();
  const setURLParams = useURLParamsSetter();
  const { reFetchAllDependencyData } = useContext(CameraStatusUpdateContext);

  // State
  const [showComment, setShowComment] = useState(false);
  // Form
  const { control, watch, handleSubmit, errors, setValue } = useForm({
    resolver: yupResolver(cameraCheckValidation(t, showComment)),
  });

  const selectState = watch("status");

  // Mutations
  const [addNotificationMutation] = useAddNotificationMutation({});
  const [setCameraCheck] = useSetCameraCheckMutation({
    onCompleted(res) {
      const newDialog = get(
        res,
        "CameraSetter.createCameraCheck"
      ) as CameraCheck;

      if (newDialog && newDialog.status !== CameraCheckStatus.Compliant)
        setURLParams({ dialog: newDialog.id });

      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.success,
          content:
            selectState.value === CameraCheckStatus.Compliant
              ? "Положение камеры согласовано"
              : "Замечание успешно добавлено",
        },
      });

      reFetchAllDependencyData();
    },
  });

  const onSubmit = (data) => {
    setCameraCheck({
      variables: {
        ...data,
        cameraId,
        status: data.status.value,
      },
    });
  };

  useEffect(() => {
    if (!!selectState && selectState.value === CameraCheckStatus.NotCompliant) {
      setShowComment(true);
    } else {
      setShowComment(false);
    }
  }, [selectState]);

  useEffect(() => {
    if (onlyNotCompliant)
      setValue("status", {
        label: "Не соответствует",
        value: CameraCheckStatus.NotCompliant,
      });
  }, [onlyNotCompliant]);

  return (
    <div className={s.subModalContainer}>
      <div className={s.subModalHeader}>
        <h4>Согласование соответствия</h4>
        {!!onlyNotCompliant && (
          <>
            <p>
              По скольку у камеры есть не решенные замечания, согласовать ее
              сейчас без решения замечаний невозможно, но вы можете добавить
              новое замечание.
            </p>
            <span>Количество замечаний: {onlyNotCompliant}</span>
          </>
        )}
      </div>
      <form className={s.subModalForm} onSubmit={handleSubmit(onSubmit)}>
        <Select
          optionObjects={
            currentStatus ? options.filter((el) => el.value !== 0) : options
          }
          control={control}
          name={"status"}
          label={"Соответствие"}
          error={errors?.compliance}
          disabled={!!onlyNotCompliant}
        />
        {showComment && (
          <TextArea
            name={"comment"}
            label={"Комментарий"}
            control={control}
            error={errors?.comment?.message}
          />
        )}
        <Button
          isForm
          txtBtn={"Сохранить"}
          customClassName={s.subModalBtn}
          disabled={
            !selectState ||
            selectState?.value === 0 ||
            (currentStatus === CameraStatus.Compliant &&
              currentStatus === selectState?.value)
          }
        />
      </form>
    </div>
  );
};

export default CameraCheckAccepting;
